import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Anchor, Users, Building, Target } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const ChineseEcommerceArticle = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-6">
          <Button 
            variant="ghost" 
            onClick={() => window.history.back()}
            className="hover:bg-gray-100"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('common.backToHighlights')}
          </Button>
          <LanguageSwitcher />
        </div>

        <article className="max-w-4xl mx-auto">
          <header className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              <Badge className="bg-red-500 text-white">
                <Anchor className="h-3 w-3 mr-1" />
                {t('article.chineseecom.badge')}
              </Badge>
              <span className="text-gray-500">{t('article.chineseecom.date')}</span>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {t('article.chineseecom.title')}
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              {t('article.chineseecom.summary')}
            </p>
          </header>

          <div className="mb-8">
            <img 
              src="https://imgs.newseditor.aws.kuehne-nagel.cloud/9c5e4638-a813-497a-b028-cfdd8fbe124d.jpg"
              alt="European logistics strike disruption and coordination" 
              className="w-full h-64 object-cover rounded-lg shadow-lg"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <Users className="h-5 w-5 mr-2 text-red-600" />
                  {t('chineseecom.stats.players.label')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold text-red-600">{t('chineseecom.stats.players.value')}</div>
                <p className="text-sm text-gray-600">{t('chineseecom.stats.players.desc')}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <Building className="h-5 w-5 mr-2 text-blue-600" />
                  {t('chineseecom.stats.warehouse.label')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold text-blue-600">{t('chineseecom.stats.warehouse.value')}</div>
                <p className="text-sm text-gray-600">{t('chineseecom.stats.warehouse.desc')}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <Target className="h-5 w-5 mr-2 text-green-600" />
                  {t('chineseecom.stats.strategy.label')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold text-green-600">{t('chineseecom.stats.strategy.value')}</div>
                <p className="text-sm text-gray-600">{t('chineseecom.stats.strategy.desc')}</p>
              </CardContent>
            </Card>
          </div>

          <div className="prose max-w-none">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('chineseecom.section1.title')}</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('chineseecom.section1.content')}
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('chineseecom.section2.title')}</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('chineseecom.section2.content')}
            </p>
            <div className="whitespace-pre-line text-gray-700 mb-6 pl-6">
              {t('chineseecom.section2.list')}
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('chineseecom.section3.title')}</h3>
            <div className="bg-blue-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">{t('chineseecom.section3.subtitle')}</h4>
              <div className="whitespace-pre-line text-sm text-gray-700">
                {t('chineseecom.section3.content')}
              </div>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('chineseecom.section4.title')}</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('chineseecom.section4.content')}
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('chineseecom.section5.title')}</h3>
            <div className="bg-orange-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">{t('chineseecom.section5.subtitle')}</h4>
              <div className="whitespace-pre-line text-sm text-gray-700">
                {t('chineseecom.section5.content')}
              </div>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('chineseecom.section6.title')}</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('chineseecom.section6.content')}
            </p>

            <div className="bg-gray-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">Source Information:</h4>
              <p className="text-sm text-gray-600 mb-2">
                {t('chineseecom.source')}
              </p>
              <Button 
                variant="outline" 
                size="sm" 
                className="text-blue-600 border-blue-200"
                onClick={() => window.open('https://mykn.kuehne-nagel.com/news/article/one-and-premier-alliance-unveil-2026-service', '_blank')}
              >
                View Original Source
              </Button>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
};

export default ChineseEcommerceArticle;